import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import WineFormDialog from "@/components/admin/WineFormDialog";

import { API } from "@/lib/apiBase";

export default function AdminWinesTab() {
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/wines`);
      setWines(r.data.wines);
    } catch (err) {
      console.error("Failed to load wines", err);
      toast.error("Failed to load wines");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = filterWines(wines, search);

  return (
    <div className="space-y-4" data-testid="admin-wines">
      <WineToolbar
        search={search}
        onSearchChange={setSearch}
        onSaved={() => { toast.success("Wine saved"); refresh(); }}
      />
      <div className="glass-card rounded-sm overflow-hidden">
        <Table>
          <WineTableHeader />
          <TableBody>
            {loading ? (
              <EmptyRow text="Loading..." />
            ) : filtered.length === 0 ? (
              <EmptyRow text="No wines found" />
            ) : (
              filtered.map((w) => (
                <WineRow key={w.id} wine={w} onChange={refresh} />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function filterWines(wines, search) {
  if (!search) return wines;
  const q = search.toLowerCase();
  return wines.filter((w) =>
    w.name?.toLowerCase().includes(q) ||
    w.producer?.toLowerCase().includes(q) ||
    w.region?.toLowerCase().includes(q)
  );
}

function WineToolbar({ search, onSearchChange, onSaved }) {
  return (
    <div className="flex items-center gap-3">
      <Input
        data-testid="admin-wines-search"
        placeholder="Search wines..."
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="max-w-sm bg-wine-paper border-white/10 text-white/90 h-10 font-body text-sm"
      />
      <div className="ml-auto">
        <WineFormDialog
          trigger={
            <Button data-testid="admin-add-wine" className="bg-merlot hover:bg-merlot-light text-white gap-2">
              <Plus className="w-4 h-4" /> Add Wine
            </Button>
          }
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}

function WineTableHeader() {
  const cols = ["Name", "Year", "Type", "Region"];
  return (
    <TableHeader>
      <TableRow className="border-white/5 hover:bg-transparent">
        {cols.map((c) => (
          <TableHead key={c} className="text-white/40 font-body text-xs uppercase tracking-wider">{c}</TableHead>
        ))}
        <TableHead className="text-white/40 font-body text-xs uppercase tracking-wider text-right">Rating</TableHead>
        <TableHead className="text-white/40 font-body text-xs uppercase tracking-wider text-right">Price</TableHead>
        <TableHead className="text-white/40 font-body text-xs uppercase tracking-wider text-right">Actions</TableHead>
      </TableRow>
    </TableHeader>
  );
}

function EmptyRow({ text }) {
  return (
    <TableRow>
      <TableCell colSpan={7} className="text-white/40 font-body py-8 text-center">{text}</TableCell>
    </TableRow>
  );
}

function WineRow({ wine, onChange }) {
  const remove = async () => {
    try {
      await axios.delete(`${API}/admin/wines/${wine.id}`);
      toast.success(`Deleted "${wine.name}"`);
      onChange();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete wine");
    }
  };

  return (
    <TableRow className="border-white/5 hover:bg-white/[0.02]" data-testid={`wine-row-${wine.id}`}>
      <TableCell className="text-white/85 font-body text-sm">{wine.name}</TableCell>
      <TableCell className="text-white/60 font-body text-sm">{wine.year}</TableCell>
      <TableCell>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-white/15 text-white/60">
          {wine.wine_type}
        </Badge>
      </TableCell>
      <TableCell className="text-white/60 font-body text-sm">{wine.region}</TableCell>
      <TableCell className="text-gold/80 font-body text-sm text-right">{wine.rating}</TableCell>
      <TableCell className="text-white/80 font-body text-sm text-right">${wine.price?.toFixed(2)}</TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-1">
          <WineFormDialog
            initial={wine}
            trigger={
              <Button variant="ghost" size="icon" data-testid={`edit-wine-${wine.id}`} className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/5">
                <Pencil className="w-4 h-4" />
              </Button>
            }
            onSaved={() => { toast.success("Wine updated"); onChange(); }}
          />
          <ConfirmDeleteButton
            testId={`delete-wine-${wine.id}`}
            confirmTestId={`confirm-delete-wine-${wine.id}`}
            title="Delete wine?"
            description={`"${wine.name}" will be permanently removed along with any user favorites referencing it.`}
            onConfirm={remove}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

function ConfirmDeleteButton({ testId, confirmTestId, title, description, onConfirm, disabled }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          data-testid={testId}
          className="h-8 w-8 text-merlot-light hover:text-red-400 hover:bg-merlot/10 disabled:opacity-30"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-wine-paper border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white/90 font-display">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-white/50 font-body">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="border-white/10 text-white/70 bg-transparent">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={confirmTestId} className="bg-merlot hover:bg-merlot-light">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { ConfirmDeleteButton };
